import { apiClient } from "@/services/apiClient";

export type PlatformRole = "root" | "root_admin";

export interface PlatformUser {
  id: string;
  email: string;
  displayName: string | null;
  platformRole: PlatformRole;
  status: string;
  createdAt: string;
}

export interface ListPlatformUsersParams {
  page: number;
  limit: number;
}

export interface ListPlatformUsersResult {
  data: PlatformUser[];
  total: number;
  page: number;
  limit: number;
}

export interface PendingRootInvite {
  id: string;
  email: string;
  platformRole: PlatformRole;
  invitedByUserId: string;
  invitedByEmail: string | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
}

export function listPlatformUsers(
  params: ListPlatformUsersParams
): Promise<ListPlatformUsersResult> {
  return apiClient
    .get<ListPlatformUsersResult>("/platform/users", {
      params: { page: params.page, limit: params.limit },
    })
    .then((res) => res.data);
}

export function listPendingRootInvites(): Promise<PendingRootInvite[]> {
  return apiClient
    .get<PendingRootInvite[]>("/platform/root-invites")
    .then((res) => res.data);
}

export function inviteRootUser(payload: {
  email: string;
  platformRole: PlatformRole;
}): Promise<void> {
  return apiClient
    .post("/auth/root-invite", {
      email: payload.email.trim().toLowerCase(),
      platformRole: payload.platformRole,
    })
    .then(() => undefined);
}

export function resendRootInvite(id: string): Promise<void> {
  return apiClient
    .post(`/platform/root-invites/${id}/resend`)
    .then(() => undefined);
}

export function cancelRootInvite(id: string): Promise<void> {
  return apiClient
    .delete(`/platform/root-invites/${id}`)
    .then(() => undefined);
}

export function deletePlatformUser(id: string): Promise<void> {
  return apiClient.delete(`/platform/users/${id}`).then(() => undefined);
}

/** One saved address, as the admin requester dialog reads it. */
export interface PlatformUserAddress {
  id: string;
  label: string | null;
  formattedAddress: string | null;
  city: string | null;
  adminArea: string | null;
  countryCode: string;
  contactPhone: string | null;
  contactPersonName: string | null;
  courierNotes: string | null;
  isDefault: boolean;
}

/**
 * The admin view of ANY person — distinct from `PlatformUser` above, which is
 * the platform-operator row the users page lists (root / root_admin only).
 * This one is for the requester behind a request or RFQ, who is usually a
 * plain consumer.
 *
 * Mirrors `PlatformUserDetailDto` on the server, which is built field by field
 * rather than spread from the user row — so there is deliberately no
 * `passwordHash` here, and no fiscal/professional credential block (spec
 * 218021de, control C-1). If a field you expect is missing, the server chose
 * not to send it; do not add it here without adding it there, or the type will
 * claim a field the response never carries.
 */
export interface PlatformUserDetail {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  phone: string | null;
  documentType: "national_id" | "passport" | null;
  documentNumber: string | null;
  identityVerifiedAt: string | null;
  status: string;
  platformRole: string | null;
  emailVerifiedAt: string | null;
  /**
   * The profession this user practises, or null. The backend has emitted this
   * since slice-3d and says beside it that "a third profession shows there and
   * in neither boolean" — the portal simply never declared it, which is why
   * the detail dialog could only name two.
   */
  profession: string | null;
  /** Derived, and NEVER widened past the two — see is-professional.ts. */
  isNotary: boolean;
  isAccountant: boolean;
  addresses: PlatformUserAddress[];
}

export function getPlatformUser(id: string): Promise<PlatformUserDetail> {
  return apiClient.get<PlatformUserDetail>(`/platform/users/${id}`).then((res) => res.data);
}
